// Friends list panel (Phase 7 Block F).
//
// HTML overlay — F key opens/closes. Shows friends with online status,
// an add-by-display-name input, and a remove button per row. Online
// status arrives via `friendsListUpdated` durable push from the server.
//
// Layout: fixed overlay, similar position to inventory modal, z-index
// above HUD but below expedition-modal.

import type { ClientMessage } from "../../shared/messages.ts";
import { buildAddFriendMessage, buildRemoveFriendMessage, type FriendEntry } from "../../shared/friends-messages.ts";

const PANEL_BG = "rgba(10,10,30,0.96)";
const BORDER = "#4455cc";
const TEXT = "#eeeeff";
const MUTED = "#8888aa";
const ONLINE_DOT = "#55ee55";
const OFFLINE_DOT = "#666688";

export type FriendsUIDeps = {
  send: (msg: ClientMessage) => void;
  onOpen: () => void;
  onClose: () => void;
};

export class FriendsUI {
  private readonly panel: HTMLDivElement;
  private readonly list: HTMLDivElement;
  private readonly nameInput: HTMLInputElement;
  private _isOpen = false;
  private _friends: FriendEntry[] = [];

  constructor(private readonly deps: FriendsUIDeps) {
    this.panel = document.createElement("div");
    this.panel.id = "friends-panel";
    this.panel.style.cssText = [
      "position:fixed",
      "top:50%",
      "left:50%",
      "transform:translate(-50%,-50%)",
      "width:320px",
      "max-height:480px",
      "background:" + PANEL_BG,
      `border:2px solid ${BORDER}`,
      "border-radius:6px",
      "padding:12px",
      "z-index:7500",
      "display:none",
      "flex-direction:column",
      "gap:8px",
      "font-family:monospace",
      "color:" + TEXT,
    ].join(";");

    const title = document.createElement("div");
    title.textContent = "Friends";
    title.style.cssText = "font-size:16px;font-weight:bold;border-bottom:1px solid #4455cc;padding-bottom:6px";
    this.panel.appendChild(title);

    this.list = document.createElement("div");
    this.list.style.cssText = "display:flex;flex-direction:column;gap:4px;overflow-y:auto;max-height:300px;";
    this.panel.appendChild(this.list);

    // Add-friend row
    const addRow = document.createElement("div");
    addRow.style.cssText = "display:flex;gap:6px;margin-top:4px;";

    this.nameInput = document.createElement("input");
    this.nameInput.type = "text";
    this.nameInput.placeholder = "Display name…";
    this.nameInput.maxLength = 20; // contracts:allow-literal — display-name max
    this.nameInput.style.cssText = [
      "flex:1",
      "background:#1a1a3a",
      "border:1px solid #4455cc",
      "border-radius:3px",
      "color:#eeeeff",
      "font-family:monospace",
      "font-size:12px",
      "padding:3px 6px",
    ].join(";");
    // Prevent the F/ESC keys from propagating and toggling the panel.
    this.nameInput.addEventListener("keydown", (e) => e.stopPropagation());

    const addBtn = document.createElement("button");
    addBtn.textContent = "Add";
    addBtn.style.cssText = "background:#2a2a55;border:1px solid #4455cc;color:#eeeeff;font-family:monospace;font-size:12px;border-radius:3px;padding:2px 10px;cursor:pointer;";
    addBtn.addEventListener("click", () => this.handleAdd());
    this.nameInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.stopPropagation(); this.handleAdd(); }
    });

    addRow.appendChild(this.nameInput);
    addRow.appendChild(addBtn);
    this.panel.appendChild(addRow);

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "Close";
    closeBtn.style.cssText = "background:#2a2a55;border:1px solid #4455cc;color:#8888aa;font-family:monospace;font-size:11px;border-radius:3px;padding:2px 8px;cursor:pointer;align-self:flex-end;margin-top:4px;";
    closeBtn.addEventListener("click", () => this.close());
    this.panel.appendChild(closeBtn);

    document.body.appendChild(this.panel);
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  open(): void {
    this._isOpen = true;
    this.panel.style.display = "flex";
    this.render();
    this.deps.onOpen();
  }

  close(): void {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.panel.style.display = "none";
    this.deps.onClose();
  }

  toggle(): void {
    if (this._isOpen) this.close();
    else this.open();
  }

  applyFriendsList(friends: FriendEntry[]): void {
    this._friends = friends;
    if (this._isOpen) this.render();
  }

  private render(): void {
    while (this.list.firstChild) this.list.removeChild(this.list.firstChild);
    if (this._friends.length === 0) {
      const empty = document.createElement("div");
      empty.textContent = "No friends yet. Add by display name.";
      empty.style.cssText = `font-size:12px;color:${MUTED};padding:6px 0;`;
      this.list.appendChild(empty);
      return;
    }
    for (const friend of this._friends) {
      this.list.appendChild(this.buildRow(friend));
    }
  }

  private buildRow(friend: FriendEntry): HTMLDivElement {
    const row = document.createElement("div");
    row.style.cssText = "display:flex;align-items:center;gap:8px;background:#1a1a3a;border-radius:3px;padding:4px 8px;";

    const dot = document.createElement("span");
    dot.style.cssText = `width:8px;height:8px;border-radius:50%;background:${friend.online ? ONLINE_DOT : OFFLINE_DOT};flex-shrink:0;display:inline-block;`;
    dot.title = friend.online ? "Online" : "Offline";

    const name = document.createElement("span");
    name.textContent = friend.displayName || friend.playerId;
    name.style.cssText = `flex:1;font-size:12px;color:${TEXT};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;

    const rmBtn = document.createElement("button");
    rmBtn.textContent = "×";
    rmBtn.title = "Remove friend";
    rmBtn.style.cssText = "background:transparent;border:none;color:#ff6666;cursor:pointer;font-size:14px;padding:0 2px;line-height:1;";
    rmBtn.addEventListener("click", () => {
      this.deps.send(buildRemoveFriendMessage(friend.playerId));
    });

    row.appendChild(dot);
    row.appendChild(name);
    row.appendChild(rmBtn);
    return row;
  }

  private handleAdd(): void {
    const name = this.nameInput.value.trim();
    if (!name) return;
    this.deps.send(buildAddFriendMessage(name));
    this.nameInput.value = "";
  }
}
